import React from 'react';

import Slider from '../../library/slider/slider';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import Counter from './components/counter/counter';
import ClockRegularIcon from '../../shared/icons/clock-regular-icon';
import MilesPerYearInput from '../miles-per-year-input/miles-per-year-input';

import {
  useAemContent,
  useTireLifeInfo,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { isWinterTire } from '../../shared/utils/product/product';
import {
  calculateLifeExpectancy,
  calculateWinterSeasons
} from '../../shared/utils/treadwell/treadwell';

import { Maybe, ProductData } from '../../shared/types/graphql-types';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../shared/constants/cms';

import './tire-life-span-info.scss';

interface TireLifeSpanInfoProps {
  max: number;
  min: number;
  months?: number;
  product?: ProductData;
  showTooltip?: boolean;
  winterDaysPerYear?: Maybe<number>;
  years?: number;
}

function TireLifeSpanInfo(props: TireLifeSpanInfoProps) {
  const {
    max,
    min,
    months,
    product,
    showTooltip = false,
    winterDaysPerYear = 0,
    years
  } = props;

  const {
    miles,
    milesInputError,
    onMilesInputBlur,
    onMilesInputChange,
    setMiles
  } = useTireLifeInfo();
  const { treadwellPreferences } = useTreadwellPreferences();

  const isWinterTreadwellFlow = Boolean(treadwellPreferences?.showWinterTires);

  const { mileage } = product?.recommendationData || product?.tdsData || {};
  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );
  const winterSeasonContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.winterSeason];
  const yearsAndMonthsContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.yearsAndMonths];

  const { tireLifeMonths, tireLifeYears } = calculateLifeExpectancy(
    product,
    15 * 1000,
    miles * 1000,
    months,
    years
  );

  const winterSeasons = calculateWinterSeasons({
    medianTireLifeEstimate: mileage?.median || 0,
    milesDrivenPerYear: miles * 1000,
    winterDaysPerYear: Number(winterDaysPerYear)
  });

  const isWinterData =
    isWinterTire(product) &&
    isWinterTreadwellFlow &&
    Number(winterDaysPerYear) > 0 &&
    winterSeasons > 0;

  return (
    <div styleName="container">
      <div styleName="title">
        <ClockRegularIcon styleName="icon" />
        <Typography weight="bold">Tire Lifespan</Typography>
        {showTooltip && (
          <Tooltip
            content={
              isWinterData
                ? `${winterSeasonContent?.tipText} ${treadwellPreferences?.zipCodeValue}`
                : yearsAndMonthsContent?.tipText
            }
            styleName="tooltip"
          />
        )}
      </div>
      <div styleName="slider">
        <Slider
          markerPrefix={`${min}K`}
          markerSuffix={`${max}K`}
          max={max}
          min={min}
          onChange={e => setMiles(e)}
          showTooltip
          value={miles}
        />
      </div>
      <MilesPerYearInput
        defaultValue={miles}
        error={milesInputError}
        label="Slide above or type in your miles driven per year"
        max={max}
        min={min}
        onBlur={onMilesInputBlur}
        onChange={onMilesInputChange}
        styleName="miles-input"
        value={miles}
      />
      {isWinterData ? (
        <div styleName="winter-container">
          <Counter countTo={winterSeasons} isWholeNum start>
            {({ value }) => <span styleName="value">{value}</span>}
          </Counter>
          <span styleName="label">winter seasons</span>
        </div>
      ) : (
        <div styleName="year-month-container">
          {typeof tireLifeYears === 'number' && (
            <div>
              <Counter countTo={tireLifeYears} isWholeNum start>
                {({ value }) => <span styleName="value">{value}</span>}
              </Counter>
              <span styleName="label">years</span>
            </div>
          )}

          <div>
            {typeof tireLifeMonths === 'number' ? (
              <Counter countTo={tireLifeMonths} isWholeNum start>
                {({ value }) => <span styleName="value">{value}</span>}
              </Counter>
            ) : (
              <span styleName="na">N/A</span>
            )}
            <span styleName="label">months</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TireLifeSpanInfo;
